/**
 * The toast (beta): one quiet card at the top of the screen when an army is launched at the learner or a raid they
 * sent comes back, only where the news would otherwise be hidden (`toastAllowed`: home behind a sheet, or a moment's
 * screen). It mirrors the two push kinds, says a name and a count and nothing else, and has the same one button as
 * home's raid line (**Defend** or **Read**).
 *
 * It rides the live updates (`live.ts`): a callback reads `GET /api/raids` whenever the digest moves, and what the
 * read holds that the last one did not is the event (`live-events.ts`). A read that finds the toast unwelcome (a
 * card, a recording, a screen with its own raids) still moves the baseline, so nothing is announced late; home's
 * line and Friends show it. Mounted once from `main.ts`, appended to the body, so it follows the learner everywhere.
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { tParts } from '../bidi-html';
import { go, parse } from '../router';
import { onSession, session } from '../session';
import { subscribeLive } from '../live';
import { newEvents, seenOf, toastAllowed, type LiveEvent, type Seen } from '../live-events';
import { raidApi } from './raid/raid-api';

/** Long enough to read a line and reach the button; it waits while a finger or the keyboard is on it. */
const SHOW_MS = 8000;

@customElement('live-toast')
export class LiveToast extends LitElement {
  @state() private event?: LiveEvent;

  /** The account being followed: beta, confirmed by the server. A read that answers after a change of account is dropped. */
  private owner?: string;
  /** The last read's raids. `data-ready` on the element (for the browser journeys) says it exists: only then is anything an event. */
  private seen?: Seen;
  private stopLive?: () => void;
  private stopSession?: () => void;
  private hideTimer?: number;
  private classes?: MutationObserver;

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    this.stopSession = onSession(() => this.follow());
    this.classes = new MutationObserver(() => this.recheck());
    this.classes.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('popstate', this.recheck);
    this.follow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopSession?.();
    this.classes?.disconnect();
    window.removeEventListener('popstate', this.recheck);
    this.unfollow();
  }

  private follow() {
    const s = session();
    const owner = s.status === 'known' && !s.provisional && s.beta ? s.owner : undefined;
    if (owner === this.owner) return;
    this.unfollow();
    this.owner = owner;
    if (owner) this.stopLive = subscribeLive(() => this.read(owner));
  }

  private unfollow() {
    this.stopLive?.();
    this.stopLive = undefined;
    this.owner = undefined;
    this.seen = undefined;
    delete this.dataset.ready;
    this.hide();
  }

  private async read(owner: string) {
    const overview = await raidApi.overview();
    if (this.owner !== owner) return;
    const events = newEvents(this.seen, overview);
    this.seen = seenOf(overview);
    this.dataset.ready = '';
    if (events.length && this.allowed()) this.show(events[0]);
  }

  private allowed() {
    return toastAllowed(parse(location.pathname, location.search).name, [...document.documentElement.classList]);
  }

  /** The learner moved on (a sheet closed, a card opened) while a toast was up. */
  private recheck = () => {
    if (this.event && !this.allowed()) this.hide();
  };

  private show(event: LiveEvent) {
    this.event = event;
    this.arm();
  }

  private hide = () => {
    window.clearTimeout(this.hideTimer);
    this.hideTimer = undefined;
    this.event = undefined;
  };

  private arm = () => {
    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(this.hide, SHOW_MS);
  };

  private pause = () => {
    window.clearTimeout(this.hideTimer);
    this.hideTimer = undefined;
  };

  private open(e: LiveEvent) {
    this.hide();
    go(`/raid/${encodeURIComponent(e.id)}`);
  }

  render() {
    const e = this.event;
    return html`<div class="live-toast" role="status" aria-live="polite">${e ? this.card(e) : nothing}</div>`;
  }

  private card(e: LiveEvent) {
    const name = { name: html`<em><bdi dir="auto">${e.name}</bdi></em>` };
    const line = e.kind === 'army'
      ? e.weapons === 1 ? tParts('raid.home.armyOne', name) : tParts('raid.home.army', name, { n: e.weapons })
      : tParts('raid.home.reportOn', name);
    return html`<div class="raid-gate live-toast-card" data-kind=${e.kind} @pointerenter=${this.pause} @pointerleave=${this.arm}
      @focusin=${this.pause} @focusout=${this.arm}>
      <span class="raid-gate-line">${line}</span>
      <button class="btn primary raid-gate-go" @click=${() => this.open(e)}>${t(e.kind === 'army' ? 'raid.defend' : 'raid.read')}</button>
      <button class="live-toast-x" aria-label=${t('common.dismiss')} @click=${this.hide}>×</button>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'live-toast': LiveToast }
}
